import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Button } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { SUPPORTED_LANGUAGES } from '../../constants/languages';
import styles from './LandingPage.module.css';
import { isLegacyWebtransferBrand } from '../../utils/miniAppBrand';

export const LandingPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  const currentLang = (i18n.language || 'en').split('-')[0].toLowerCase();
  const isRTL = currentLang === 'ar';

  const handleLanguageChange = (selectedLang: string) => {
    i18n.changeLanguage(selectedLang);
    try {
      localStorage.setItem('language', selectedLang);
      document.documentElement.lang = selectedLang;
      document.documentElement.dir = selectedLang === 'ar' ? 'rtl' : 'ltr';
    } catch (e) {
      // Ignore localStorage errors
    }
  };

  const handleOpenTelegram = () => {
    window.open('https://t.me/Webtransfer_apps_bot', '_self');
  };

  return (
    <div className={styles.landingContainer} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Top Bar: Language Selector */}
      <div className={styles.topBar}>
        <div className={styles.langSelectorWrapper}>
          <GlobalOutlined className={styles.globeIcon} />
          <Select
            value={currentLang}
            onChange={handleLanguageChange}
            className={styles.langSelect}
            bordered={false}
            popupMatchSelectWidth={false}
            dropdownStyle={{
              background: '#1c2128',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow: '0 12px 28px rgba(0, 0, 0, 0.6)',
              padding: '6px',
            }}
            options={SUPPORTED_LANGUAGES.map((lang) => ({
              value: lang.code,
              label: (
                <span className={styles.langOption}>
                  <span className={styles.flag}>{lang.flag}</span>
                  <span>{lang.nativeName}</span>
                </span>
              ),
            }))}
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className={styles.mainCard}>
        <div className={styles.logoWrapper}>
          <img
            src={isLegacyWebtransferBrand ? '/assets/LogoW.png' : '/assets/kredafi_k_only.png'}
            alt={isLegacyWebtransferBrand ? 'Webtransfer Logo' : 'KredaFi Logo'}
            className={styles.logo}
            onError={(e) => {
              (e.target as HTMLImageElement).src = isLegacyWebtransferBrand
                ? '/assets/web-logo.png'
                : '/assets/kredafi-icon-192.png';
            }}
          />
        </div>

        <h1 className={styles.title}>{isLegacyWebtransferBrand ? 'Webtransfer' : 'KredaFi'}</h1>
        <div className={styles.subtitle}>{t('entryTitle')}</div>

        <p className={styles.description}>
          {t('entryDescription')}
        </p>

        {/* Primary CTA Button */}
        <Button
          type="primary"
          size="large"
          className={styles.telegramBtn}
          onClick={handleOpenTelegram}
        >
          <svg className={styles.telegramIcon} viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.19-.08-.05-.19-.02-.27 0-.12.03-1.99 1.27-5.61 3.72-.53.36-1.01.54-1.44.53-.47-.01-1.38-.27-2.05-.48-.83-.27-1.48-.42-1.42-.88.03-.24.38-.49 1.03-.75 4.04-1.76 6.74-2.92 8.09-3.48 3.85-1.6 4.65-1.88 5.17-1.89.11 0 .37.03.54.17.14.12.18.28.2.45-.02.07-.02.21-.04.36z" />
          </svg>
          <span>{t('openInTelegram')}</span>
        </Button>

        {/* Community & News Links */}
        <div className={styles.linksRow}>
          <a
            href="https://t.me/webtransfercredit"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.secondaryLink}
          >
            {t('officialChannel')}
          </a>
          <span className={styles.linkDivider}>•</span>
          <a
            href="https://t.me/webtransferp2p"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.secondaryLink}
          >
            {t('communityChat')}
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        © {new Date().getFullYear()} {isLegacyWebtransferBrand ? 'Webtransfer' : 'KredaFi'}. All rights reserved.
      </div>
    </div>
  );
};

export default LandingPage;
